/*
 * Zapiet Pickup + Delivery + Shipping ("Out of Town") selector for the product page.
 *
 * Reads availability from Zapiet's storefront API (the same endpoints the
 * Zapiet cart widget uses) and, on Add to Cart, writes the selection in the
 * exact shape the Zapiet cart widget writes:
 *   - cart attributes (Checkout-Method, Pickup-* / Delivery-* / Shipping-*; every other key blanked)
 *   - _ZapietId line property on the new line and on every shippable line already in the cart
 *   - localStorage "zapiet_cart_session" so the cart widget can restore the delivery zip
 * The cart widget only restores this selection when Zapiet's
 * "Enable restoring widget selection from the ZapietID" setting is on.
 */
(function () {
  'use strict';

  var API_HOST = 'https://api.zapiet.com/';
  var MAX_DATES = 45;           // how many available dates to list
  var MAX_LOOKAHEAD_DAYS = 120; // when Zapiet returns no maxDate

  var ATTRIBUTE_KEYS = [
    'Pickup-Location-Id', 'Pickup-Day', 'Pickup-Date', 'Pickup-Time', 'Pickup-Note',
    'Delivery-Location-Id', 'Delivery-Day', 'Delivery-Date', 'Delivery-Time', 'Delivery-Note',
    'Shipping-Day', 'Shipping-Date', 'Shipping-Time', 'Shipping-Note',
    'Pickup-Location-Company', 'Pickup-Location-Address-Line-1', 'Pickup-Location-Address-Line-2',
    'Pickup-Location-City', 'Pickup-Location-Region', 'Pickup-Location-Postal-Code',
    'Pickup-Location-Country', 'Pickup-Location-Phone',
    'Delivery-Slot-Id', 'Shipping-Slot-Id', 'Checkout-Method',
    'Custom-Attribute-1', 'Custom-Attribute-2', 'Custom-Attribute-3', 'SMS-Consent'
  ];

  var DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /* ---------- helpers ---------- */

  function shopDomain() {
    return (window.Shopify && window.Shopify.shop) || '';
  }

  function api(path, body) {
    return fetch(API_HOST + path + '?shop=' + encodeURIComponent(shopDomain()), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      if (!r.ok) throw new Error('Zapiet API ' + r.status);
      return r.json();
    });
  }

  // "2026-09-30" -> {y, m (1-12), d}
  function parseIso(iso) {
    var p = iso.split('-');
    return { y: +p[0], m: +p[1], d: +p[2] };
  }

  function isoFromUTC(date) {
    return date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function dateLabel(iso) {
    var p = parseIso(iso);
    var dt = new Date(Date.UTC(p.y, p.m - 1, p.d));
    return WEEKDAYS[dt.getUTCDay()] + ', ' + MONTHS[p.m - 1] + ' ' + p.d;
  }

  // "13:00" -> "1:00 PM" (Zapiet's store time format is h:i A)
  function to12h(hhmm) {
    var p = hhmm.split(':');
    var h = +p[0];
    var suffix = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + p[1] + ' ' + suffix;
  }

  // Available dates from a Zapiet calendar response.
  // disabled: [[y, monthIndex0, d], ...] and/or weekday numbers (1 = Sunday ... 7 = Saturday).
  function availableDates(cal) {
    if (!cal || !cal.minDate) return [];
    var disabledDays = {};
    var disabledWeekdays = {};
    (cal.disabled || []).forEach(function (entry) {
      if (Array.isArray(entry)) {
        disabledDays[entry[0] + '-' + pad(entry[1] + 1) + '-' + pad(entry[2])] = true;
      } else if (typeof entry === 'number') {
        disabledWeekdays[entry - 1] = true;
      }
    });
    var openDays = cal.daysOfWeek ? Object.keys(cal.daysOfWeek) : null;

    var start = parseIso(cal.minDate);
    var cursor = new Date(Date.UTC(start.y, start.m - 1, start.d));
    var limit = new Date(cursor.getTime());
    if (cal.maxDate) {
      var max = parseIso(String(cal.maxDate).replace(/\//g, '-'));
      limit = new Date(Date.UTC(max.y, max.m - 1, max.d));
    } else {
      limit.setUTCDate(limit.getUTCDate() + MAX_LOOKAHEAD_DAYS);
    }

    var out = [];
    while (cursor <= limit && out.length < MAX_DATES) {
      var iso = isoFromUTC(cursor);
      var weekday = cursor.getUTCDay();
      var open = !openDays || openDays.indexOf(DAY_NAMES[weekday]) !== -1;
      if (open && !disabledWeekdays[weekday] && !disabledDays[iso]) out.push(iso);
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
  }

  // Current date and time in the store's time zone: {date: "2026-10-06", time: "14:05"}.
  function storeNow(timeZone) {
    try {
      var parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
      }).formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
      return { date: parts.year + '-' + parts.month + '-' + parts.day, time: parts.hour + ':' + parts.minute };
    } catch (e) {
      return null;
    }
  }

  // "60120, 60123\n60124" -> {"60120": true, ...}; null when no list is set.
  function zipList(text) {
    var zips = String(text || '').match(/\d{5}/g);
    if (!zips) return null;
    var out = {};
    zips.forEach(function (z) { out[z] = true; });
    return out;
  }

  function zapietId(sel) {
    var args = {
      method: sel.method,
      location_id: sel.location ? sel.location.id : '',
      date: sel.date.replace(/-/g, '/'),
      time: sel.slot ? to12h(sel.slot.available_from) : '',
      end_time: sel.method === 'delivery' ? sel.slot.available_until : ''
    };
    var cart = window.Zapiet && window.Zapiet.Cart;
    if (cart && typeof cart.getZapietId === 'function') return cart.getZapietId(args);
    // Fallback: same format Zapiet produces, e.g. M=P&L=287281&D=2026-09-30T12:00:00Z
    var parts = [
      'M=' + (sel.method === 'delivery' ? 'D' : sel.method === 'pickup' ? 'P' : 'S'),
      args.location_id ? 'L=' + args.location_id : '',
      'D=' + sel.date + 'T' + (sel.slot ? sel.slot.available_from : '00:00') + ':00Z',
      args.end_time ? 'E=' + args.end_time : ''
    ];
    return parts.filter(Boolean).join('&');
  }

  /* ---------- selector ---------- */

  function Selector(root) {
    this.root = root;
    this.form = document.getElementById(root.dataset.formId);
    this.product = JSON.parse(root.dataset.product || '{}');
    this.hiddenSlots = JSON.parse(root.dataset.hiddenSlots || '{}');
    this.deliveryZips = zipList(root.dataset.deliveryZips);
    this.deliveryCutoff = root.dataset.deliveryCutoff || '';

    this.els = {
      methods: root.querySelectorAll('[data-zps-method]'),
      pickupPanel: root.querySelector('[data-zps-panel="pickup"]'),
      deliveryPanel: root.querySelector('[data-zps-panel="delivery"]'),
      shippingPanel: root.querySelector('[data-zps-panel="shipping"]'),
      timeField: root.querySelector('[data-zps-time-field]'),
      when: root.querySelector('[data-zps-when]'),
      location: root.querySelector('[data-zps-location]'),
      postcode: root.querySelector('[data-zps-postcode]'),
      check: root.querySelector('[data-zps-check]'),
      shipInstead: root.querySelector('[data-zps-ship-instead]'),
      deliveryNote: root.querySelector('[data-zps-delivery-note]'),
      date: root.querySelector('[data-zps-date]'),
      time: root.querySelector('[data-zps-time]'),
      error: root.querySelector('[data-zps-error]'),
      cartNote: root.querySelector('[data-zps-cart-note]'),
      zapietIdInput: root.querySelector('[data-zps-zapiet-id]')
    };

    this.state = {
      method: null,
      pickupLocations: [],
      location: null,
      calendar: null,
      calendarFetchedAt: 0,
      slots: [],
      date: '',
      slot: null,
      postcode: ''
    };
    this.requestId = 0;
    this.submitting = false;
    // Selection saved by an earlier Add to Cart, applied step by step as each list loads.
    this.pending = null;

    this.bind();
    this.cartItems().then(function (items) {
      this.existingItems = items;
      this.restore();
    }.bind(this));
    // First enabled option (the block can turn any of them off).
    var first = Array.prototype.find.call(this.els.methods, function (btn) { return !btn.hidden; });
    if (first) this.setMethod(first.dataset.zpsMethod);
  }

  // Pre-fill the selection already saved on the cart (method, zip / location, date, time)
  // so a second product goes out with the first one. Only while the cart has items.
  Selector.prototype.restore = function () {
    var cart = this.cart;
    var a = (cart && cart.attributes) || {};
    var method = a['Checkout-Method'];
    if (!cart || !cart.item_count || ['delivery', 'pickup', 'shipping'].indexOf(method) === -1) return;
    var btn = Array.prototype.find.call(this.els.methods, function (b) { return b.dataset.zpsMethod === method; });
    if (!btn || (btn.hidden && !(method === 'shipping' && this.els.shipInstead))) return;
    this.cartMethod = method;

    var key = method === 'delivery' ? 'Delivery' : method === 'pickup' ? 'Pickup' : 'Shipping';
    var pending = {
      date: String(a[key + '-Date'] || '').replace(/\//g, '-'),
      locationId: Number(a['Pickup-Location-Id']) || null,
      slotId: Number(a['Delivery-Slot-Id']) || null,
      time: a[key + '-Time'] || ''
    };

    if (method === 'delivery') {
      var zip = '';
      try { zip = (JSON.parse(window.localStorage.getItem('zapiet_cart_session') || '{}').deliveryPostcode) || ''; } catch (e) { /* no storage */ }
      if (zip) {
        this.els.postcode.value = zip;
        this.state.postcode = zip;
      } else {
        pending = null; // no saved zip: open Local Delivery and let the shopper enter it
      }
    }

    this.pending = pending;
    this.state.method = null; // re-run setMethod even if this option is already active
    this.setMethod(method);
  };

  Selector.prototype.bind = function () {
    var self = this;
    this.els.methods.forEach(function (btn) {
      btn.addEventListener('click', function () { self.pending = null; self.setMethod(btn.dataset.zpsMethod); });
    });
    this.els.location.addEventListener('change', function () {
      self.pending = null;
      var id = Number(self.els.location.value);
      self.state.location = self.state.pickupLocations.find(function (l) { return l.id === id; }) || null;
      self.loadCalendar();
    });
    this.els.check.addEventListener('click', function () { self.pending = null; self.checkPostcode(); });
    if (this.els.shipInstead) {
      this.els.shipInstead.addEventListener('click', function () { self.pending = null; self.setMethod('shipping'); });
    }
    this.els.postcode.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); self.checkPostcode(); }
    });
    // Editing the zip invalidates the previous check until Check is pressed again.
    this.els.postcode.addEventListener('input', function () {
      if (self.els.postcode.value.trim() === self.state.postcode) return;
      self.pending = null;
      self.state.postcode = '';
      self.state.location = null;
      self.els.deliveryNote.hidden = true;
      self.hideShipInstead();
      self.resetWhen();
    });
    this.els.date.addEventListener('change', function () { self.pending = null; self.loadSlots(self.els.date.value); });
    this.els.time.addEventListener('change', function () {
      var id = Number(self.els.time.value);
      self.state.slot = self.state.slots.find(function (s) { return s.id === id; }) || null;
      self.clearError();
    });
    if (this.form) {
      // Capture phase so we run before the theme's own submit handling.
      this.form.addEventListener('submit', this.onSubmit.bind(this), true);
    }
  };

  // Current product + anything already in the cart, in the shape Zapiet expects.
  Selector.prototype.shoppingCart = function () {
    var idInput = this.form && this.form.querySelector('[name="id"]');
    var variantId = idInput ? Number(idInput.value) : null;
    var items = [];
    if (variantId) {
      items.push({
        variant_id: variantId,
        product_id: this.product.product_id,
        quantity: 1,
        tags: this.product.tags || [],
        vendors: [this.product.vendor],
        product_types: [this.product.product_type]
      });
    }
    return items.concat(this.existingItems || []);
  };

  Selector.prototype.cartItems = function () {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        this.cart = cart;
        var zc = window.Zapiet && window.Zapiet.Cart;
        if (zc && typeof zc.getFormattedItems === 'function') {
          try { return zc.getFormattedItems(cart.items); } catch (e) { /* fall through */ }
        }
        return cart.items.map(function (i) {
          return { variant_id: i.variant_id, product_id: i.product_id, quantity: i.quantity, tags: [], vendors: [i.vendor], product_types: [i.product_type] };
        });
      }.bind(this))
      .catch(function () { return []; });
  };

  Selector.prototype.methodLabel = function (method) {
    var btn = Array.prototype.find.call(this.els.methods, function (b) { return b.dataset.zpsMethod === method; });
    return btn ? (btn.textContent || '').trim() : method;
  };

  // One fulfillment method per order: Add to Cart applies the new choice to every item,
  // so say so when the shopper picks something other than what the cart is set for.
  Selector.prototype.updateCartNote = function () {
    var note = this.els.cartNote;
    if (!note) return;
    var from = this.cartMethod;
    var to = this.state.method;
    if (!from || !to || from === to) {
      note.hidden = true;
      return;
    }
    note.textContent = 'Items in your cart are set for ' + this.methodLabel(from) + '. Adding this with '
      + this.methodLabel(to) + ' will change it for all items in your cart.';
    note.hidden = false;
  };

  Selector.prototype.setMethod = function (method) {
    if (this.state.method === method) return;
    this.state.method = method;
    this.state.location = null;
    this.updateCartNote();
    this.resetWhen();
    this.clearError();
    this.hideShipInstead();

    this.els.methods.forEach(function (btn) {
      var on = btn.dataset.zpsMethod === method;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    this.els.pickupPanel.hidden = method !== 'pickup';
    this.els.deliveryPanel.hidden = method !== 'delivery';
    this.els.shippingPanel.hidden = method !== 'shipping';
    // Out of Town is date-only (the store has Zapiet's shipping time picker off).
    this.els.timeField.hidden = method === 'shipping';

    if (method === 'pickup') this.loadPickupLocations();
    if (method === 'shipping') this.loadCalendar();
    if (method === 'delivery' && this.state.postcode) this.checkPostcode();
  };

  // Zip isn't in a delivery zone: offer Out of Town below the message
  // (a fallback-only Out of Town button stays out of the top row).
  Selector.prototype.offerShipping = function () {
    this.els.shipInstead.hidden = false;
  };

  Selector.prototype.hideShipInstead = function () {
    if (this.els.shipInstead) this.els.shipInstead.hidden = true;
  };

  Selector.prototype.loadPickupLocations = function () {
    var self = this;
    var req = ++this.requestId;
    var sel = this.els.location;
    sel.disabled = true;
    sel.innerHTML = '<option value="">Loading locations…</option>';

    api('v1.0/pickup/locations', { geoSearchQuery: '', shoppingCart: this.shoppingCart() })
      .then(function (res) {
        if (req !== self.requestId) return;
        var locations = res.locations || [];
        self.state.pickupLocations = locations;
        if (!locations.length) {
          sel.innerHTML = '<option value="">No pickup locations available</option>';
          return;
        }
        sel.innerHTML = locations.map(function (l) {
          return '<option value="' + l.id + '">' + escapeHtml(pickupLabel(l)) + '</option>';
        }).join('');
        sel.disabled = false;
        // Saved location from the cart, else Zapiet's "Preselect first location" setting.
        var pendingId = self.pending && self.pending.locationId;
        var loc = locations.find(function (l) { return l.id === pendingId; }) || locations[0];
        self.state.location = loc;
        sel.value = String(loc.id);
        self.loadCalendar();
      })
      .catch(function () {
        if (req !== self.requestId) return;
        sel.innerHTML = '<option value="">Could not load locations</option>';
        self.showError('We couldn\'t load pickup locations. Please try again.');
      });
  };

  Selector.prototype.checkPostcode = function () {
    var self = this;
    var zip = (this.els.postcode.value || '').trim();
    this.state.location = null;
    this.resetWhen();
    this.clearError();
    this.els.deliveryNote.hidden = true;
    this.hideShipInstead();

    if (!zip) {
      this.showError('Please enter your zip code.');
      return;
    }
    this.state.postcode = zip;

    // The store's own list of local delivery zips decides first; anything else is Out of Town.
    var zip5 = (zip.match(/\d{5}/) || [''])[0];
    if (this.deliveryZips && !this.deliveryZips[zip5]) {
      this.notDeliverable(zip);
      return;
    }

    var req = ++this.requestId;
    this.els.check.disabled = true;

    api('v1.0/delivery/locations', { geoSearchQuery: zip, shoppingCart: this.shoppingCart() })
      .then(function (res) {
        if (req !== self.requestId) return;
        if (res.error || !res.id) {
          self.notDeliverable(zip);
          return;
        }
        self.state.location = res;
        self.els.deliveryNote.textContent = 'Great! We deliver to ' + zip  + '.';
        self.els.deliveryNote.hidden = false;
        self.loadCalendar();
      })
      .catch(function () {
        if (req !== self.requestId) return;
        self.showError('We couldn\'t check that zip code. Please try again.');
      })
      .then(function () { self.els.check.disabled = false; });
  };

  Selector.prototype.notDeliverable = function (zip) {
    this.pending = null;
    if (this.els.shipInstead) {
      this.showError('Sorry, we don\'t deliver to ' + zip + '.', true);
      this.offerShipping();
    } else {
      this.showError('Sorry, we don\'t deliver to ' + zip + '. Try Store Pickup instead.', true);
    }
  };

  // Same-day local delivery closes at the cutoff time (store time zone).
  Selector.prototype.pastCutoff = function (iso) {
    if (this.state.method !== 'delivery' || !this.deliveryCutoff) return false;
    var now = storeNow((this.state.calendar && this.state.calendar.timezone) || 'America/Chicago');
    return !!now && iso === now.date && now.time >= this.deliveryCutoff;
  };

  Selector.prototype.loadCalendar = function () {
    var self = this;
    var loc = this.state.location;
    var shipping = this.state.method === 'shipping';
    this.resetWhen();
    if (!loc && !shipping) return;
    var req = ++this.requestId;
    this.els.when.hidden = false;
    this.els.date.innerHTML = '<option value="">Loading dates…</option>';

    var path = shipping ? 'v1.0/shipping/calendar' : 'v1.0/' + this.state.method + '/locations/' + loc.id + '/calendar';
    api(path, { shoppingCart: this.shoppingCart() })
      .then(function (cal) {
        if (req !== self.requestId) return;
        self.state.calendar = cal;
        self.state.calendarFetchedAt = Date.now();
        var dates = availableDates(cal).filter(function (iso) { return !self.pastCutoff(iso); });
        if (!dates.length) {
          self.els.date.innerHTML = '<option value="">No dates available</option>';
          return;
        }
        self.els.date.innerHTML = '<option value="">Select a date</option>' + dates.map(function (iso) {
          return '<option value="' + iso + '">' + dateLabel(iso) + '</option>';
        }).join('');
        self.els.date.disabled = false;
        var pending = self.pending;
        if (pending && dates.indexOf(pending.date) !== -1) {
          self.els.date.value = pending.date;
          self.loadSlots(pending.date);
        } else {
          self.pending = null; // saved date no longer offered: let the shopper pick
        }
      })
      .catch(function () {
        if (req !== self.requestId) return;
        self.els.date.innerHTML = '<option value="">Could not load dates</option>';
        self.showError('We couldn\'t load available dates. Please try again.');
      });
  };

  // Zapiet's calendar says how long it stays valid (revalidateIn, seconds).
  Selector.prototype.calendarExpired = function () {
    var cal = this.state.calendar;
    if (!cal || !cal.revalidateIn) return false;
    return Date.now() - this.state.calendarFetchedAt > cal.revalidateIn * 1000;
  };

  Selector.prototype.fetchSlots = function (iso) {
    var self = this;
    var loc = this.state.location;
    var cal = this.state.calendar || {};
    if (this.pastCutoff(iso)) return Promise.resolve([]);
    return api('v1.0/' + this.state.method + '/locations/' + loc.id + '/calendar/' + iso, {
      shoppingCart: this.shoppingCart(),
      postal_code: this.state.method === 'delivery' ? this.state.postcode : ''
    }).then(function (slots) {
      slots = Array.isArray(slots) ? slots : [];
      // Earliest day: never offer slots before Zapiet's earliest time (lead time / cutoff).
      if (cal.minDate === iso && cal.minDateTime) {
        var earliest = String(cal.minDateTime).split(' ')[1] || '00:00';
        slots = slots.filter(function (s) { return s.available_from + ':00' >= earliest; });
      }
      return self.withoutHiddenSlots(iso, slots);
    });
  };

  // Mirrors the store's Zapiet Custom JavaScript, which hides these times in the cart widget.
  Selector.prototype.withoutHiddenSlots = function (iso, slots) {
    var rules = this.hiddenSlots[this.state.method] || {};
    var hidden = rules[iso.slice(5)] || [];
    return slots.filter(function (s) { return hidden.indexOf(s.available_from) === -1; });
  };

  Selector.prototype.loadSlots = function (iso) {
    var self = this;
    this.state.date = iso;
    this.state.slot = null;
    this.state.slots = [];
    this.clearError();
    var timeSel = this.els.time;
    timeSel.disabled = true;
    if (!iso) {
      timeSel.innerHTML = '<option value="">Select a time</option>';
      return;
    }
    if (this.calendarExpired()) {
      this.loadCalendar();
      return;
    }
    if (this.state.method === 'shipping') { this.pending = null; return; } // date only, no time slots
    var req = ++this.requestId;
    timeSel.innerHTML = '<option value="">Loading times…</option>';

    this.fetchSlots(iso)
      .then(function (slots) {
        if (req !== self.requestId) return;
        self.state.slots = slots;
        if (!slots.length) {
          timeSel.innerHTML = '<option value="">No times available on this date</option>';
          return;
        }
        timeSel.innerHTML = '<option value="">Select a time</option>' + slots.map(function (s) {
          return '<option value="' + s.id + '">' + escapeHtml(self.timeLabel(s)) + '</option>';
        }).join('');
        timeSel.disabled = false;
        var pending = self.pending;
        self.pending = null;
        if (pending) {
          var saved = slots.find(function (s) {
            return s.id === pending.slotId || self.timeLabel(s) === pending.time;
          });
          if (saved) {
            self.state.slot = saved;
            timeSel.value = String(saved.id);
          }
        }
      })
      .catch(function () {
        if (req !== self.requestId) return;
        timeSel.innerHTML = '<option value="">Could not load times</option>';
        self.showError('We couldn\'t load available times. Please try again.');
      });
  };

  // Same wording the Zapiet cart widget writes: pickup "12:00 PM", delivery "9:00 AM - 2:00 PM".
  Selector.prototype.timeLabel = function (slot) {
    if (this.state.method === 'delivery') return to12h(slot.available_from) + ' - ' + to12h(slot.available_until);
    return to12h(slot.available_from);
  };

  Selector.prototype.resetWhen = function () {
    this.requestId++;
    this.state.calendar = null;
    this.state.slots = [];
    this.state.date = '';
    this.state.slot = null;
    this.els.when.hidden = true;
    this.els.date.disabled = true;
    this.els.date.innerHTML = '<option value="">Select a date</option>';
    this.els.time.disabled = true;
    this.els.time.innerHTML = '<option value="">Select a time</option>';
  };

  // notice: a friendly (gold) message, e.g. zip outside the delivery area, rather than a red error.
  Selector.prototype.showError = function (msg, notice) {
    this.els.error.textContent = msg;
    this.els.error.classList.toggle('zps__error--notice', !!notice);
    this.els.error.hidden = false;
  };

  Selector.prototype.clearError = function () {
    this.els.error.hidden = true;
    this.els.error.textContent = '';
  };

  Selector.prototype.missingStep = function () {
    var s = this.state;
    if (!s.method) return 'Please choose how you would like to receive it.';
    if (s.method === 'delivery' && !s.location) return 'Please enter your zip code and press Check.';
    if (s.method === 'pickup' && !s.location) return 'Please choose a pickup location.';
    if (!s.date) return 'Please choose a date.';
    if (s.method !== 'shipping' && !s.slot) return 'Please choose a time.';
    return null;
  };

  /* ---------- add to cart ---------- */

  Selector.prototype.onSubmit = function (evt) {
    if (this.submitting) return; // our own programmatic submit
    evt.preventDefault();
    evt.stopImmediatePropagation();

    // Let the browser show its own messages for required fields (e.g. Card Message).
    if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    var missing = this.missingStep();
    if (missing) {
      this.showError(missing);
      this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var self = this;
    var sel = { method: this.state.method, location: this.state.location, date: this.state.date, slot: this.state.slot };
    var button = this.form.querySelector('[type="submit"]');
    if (button) button.disabled = true;

    // Re-check the chosen time is still offered before committing it (shipping has no times).
    var recheck = sel.method === 'shipping'
      ? Promise.resolve(null)
      : this.fetchSlots(sel.date);
    recheck
      .then(function (slots) {
        if (!slots) return self.commit(sel);
        var still = slots.find(function (s) { return s.id === sel.slot.id; });
        if (!still) {
          var err = new Error('slot-gone');
          err.userMessage = 'That time is no longer available. Please choose another time.';
          throw err;
        }
        return self.commit(sel);
      })
      .then(function () {
        self.submitting = true;
        HTMLFormElement.prototype.submit.call(self.form);
      })
      .catch(function (err) {
        if (button) button.disabled = false;
        if (err && err.message === 'slot-gone') self.loadSlots(sel.date);
        self.showError((err && err.userMessage) || 'Something went wrong saving your delivery details. Please try again.');
      });
  };

  Selector.prototype.attributesFor = function (sel) {
    var attrs = {};
    ATTRIBUTE_KEYS.forEach(function (k) { attrs[k] = ''; });
    var loc = sel.location;
    var date = sel.date.replace(/-/g, '/');
    attrs['Checkout-Method'] = sel.method;
    if (sel.method === 'shipping') {
      attrs['Shipping-Date'] = date;
      return attrs;
    }
    if (sel.method === 'pickup') {
      attrs['Pickup-Location-Id'] = String(loc.id);
      attrs['Pickup-Date'] = date;
      attrs['Pickup-Time'] = to12h(sel.slot.available_from);
      attrs['Pickup-Location-Company'] = loc.company_name || '';
      attrs['Pickup-Location-Address-Line-1'] = loc.address_line_1 || '';
      attrs['Pickup-Location-Address-Line-2'] = loc.address_line_2 || '';
      attrs['Pickup-Location-City'] = loc.city || '';
      attrs['Pickup-Location-Region'] = loc.region || '';
      attrs['Pickup-Location-Postal-Code'] = loc.postal_code || '';
      attrs['Pickup-Location-Country'] = loc.country || '';
      attrs['Pickup-Location-Phone'] = loc.phone || '';
    } else {
      attrs['Delivery-Location-Id'] = String(loc.id);
      attrs['Delivery-Date'] = date;
      attrs['Delivery-Time'] = to12h(sel.slot.available_from) + ' - ' + to12h(sel.slot.available_until);
      attrs['Delivery-Slot-Id'] = String(sel.slot.id);
    }
    attrs['Custom-Attribute-1'] = loc.custom_attribute_1 || '';
    attrs['Custom-Attribute-2'] = loc.custom_attribute_2 || '';
    attrs['Custom-Attribute-3'] = loc.custom_attribute_3 || '';
    return attrs;
  };

  Selector.prototype.commit = function (sel) {
    var id = zapietId(sel);
    var postJson = function (url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) throw new Error(url + ' ' + r.status);
        return r.json();
      });
    };

    // The line being added carries _ZapietId through the product form.
    this.els.zapietIdInput.value = id;
    this.els.zapietIdInput.disabled = false;

    // The cart widget restores the delivery zip from here when the ZapietID matches.
    try {
      var session = { zapietId: id };
      if (sel.method === 'delivery') session.deliveryPostcode = this.state.postcode;
      window.localStorage.setItem('zapiet_cart_session', JSON.stringify(session));
    } catch (e) { /* storage unavailable: the cart widget will ask for the zip again */ }

    return postJson('/cart/update.js', { attributes: this.attributesFor(sel) })
      .then(function (cart) {
        // Rewrite _ZapietId on shippable lines already in the cart, one at a time.
        var lines = cart.items.filter(function (i) {
          return i.requires_shipping && (!i.properties || i.properties._ZapietId !== id);
        });
        return lines.reduce(function (p, item) {
          return p.then(function () {
            var props = Object.assign({}, item.properties || {}, { _ZapietId: id });
            return postJson('/cart/change.js', { id: item.key, quantity: item.quantity, properties: props });
          });
        }, Promise.resolve());
      });
  };

  /* ---------- utils ---------- */

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function shortName(loc) {
    return loc.city || loc.company_name || 'our store';
  }

  function pickupLabel(loc) {
    return (loc.city || loc.company_name) + ' — ' + [loc.address_line_1, loc.city].filter(Boolean).join(', ');
  }

  function init() {
    document.querySelectorAll('[data-zps]').forEach(function (root) {
      if (root.dataset.zpsReady) return;
      root.dataset.zpsReady = 'true';
      new Selector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
